import React, { useEffect, useState } from 'react'
import { useTranslation } from 'react-i18next'
import { Loader2, Plus, X } from 'lucide-react'
import { Card, CardContent } from '../ui/card'

// Shared add-vocab modal — used by /english/vocab (manual entry)
// and by news article surfaces (selection → "Save to English").
//
// The caller controls `open`, supplies the optional `initialWord`
// and `initialContext` (when invoked from a text selection on an
// article), and provides `onSave` which is expected to be the
// `add()` returned by useEnglishVocab. The sheet shows a spinner
// while saving and closes itself on success.
export default function EnglishVocabAddSheet({
  open,
  onClose,
  onSave,
  saving = false,
  initialWord = '',
  initialContext = '',
  sourceKind = 'manual',
  sourceUrl = null,
}) {
  const { t } = useTranslation()
  const [word, setWord] = useState(initialWord)
  const [context, setContext] = useState(initialContext)

  // Reset whenever the sheet opens with fresh initial values so
  // reopening it with a different selection doesn't keep stale text.
  useEffect(() => {
    if (open) {
      setWord(initialWord)
      setContext(initialContext)
    } else {
      setWord('')
      setContext('')
    }
  }, [open, initialWord, initialContext])

  if (!open) return null

  const submit = async (e) => {
    e?.preventDefault?.()
    const w = word.trim()
    if (!w) return
    const res = await onSave({
      word: w,
      context_sentence: context.trim() || null,
      context_source_kind: sourceKind,
      context_source_url: sourceUrl,
    })
    if (!res?.error) onClose()
  }

  return (
    <div
      className="fixed inset-0 z-50 flex items-end sm:items-center justify-center bg-black/40 px-3 pt-3"
      // Bottom padding clears the global bottom tab bar (≈64px) plus
      // the iOS home-indicator safe area, so the sheet's Save button
      // doesn't tuck behind the bar on mobile-bottom-anchored mode.
      style={{ paddingBottom: 'calc(80px + env(safe-area-inset-bottom, 0px))' }}
      onClick={onClose}
    >
      <Card className="w-full max-w-md" onClick={(e) => e.stopPropagation()}>
        <CardContent className="p-4 space-y-3">
          <div className="flex items-center justify-between">
            <h2 className="text-base font-semibold text-foreground" dir="auto">
              {t('english.vocab.addTitle')}
            </h2>
            <button type="button" onClick={onClose} aria-label={t('english.vocab.cancel')}>
              <X className="w-4 h-4 text-muted-foreground" />
            </button>
          </div>
          <form onSubmit={submit} className="space-y-2">
            <div>
              <label className="text-[11px] text-muted-foreground" dir="auto">
                {t('english.vocab.wordLabel')}
              </label>
              <input
                value={word}
                onChange={(e) => setWord(e.target.value)}
                placeholder={t('english.vocab.wordPlaceholder')}
                className="w-full px-3 py-2 mt-1 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500"
                autoFocus
                dir="auto"
              />
            </div>
            <div>
              <label className="text-[11px] text-muted-foreground" dir="auto">
                {t('english.vocab.contextLabel')}
              </label>
              <textarea
                value={context}
                onChange={(e) => setContext(e.target.value)}
                placeholder={t('english.vocab.contextPlaceholder')}
                rows={2}
                className="w-full px-3 py-2 mt-1 rounded-md border border-input bg-background text-foreground text-sm focus:outline-none focus:ring-2 focus:ring-app-primary-500 resize-none"
                dir="auto"
              />
            </div>
            <button
              type="submit"
              disabled={saving || !word.trim()}
              className="w-full inline-flex items-center justify-center gap-1.5 px-3 py-2 rounded-md bg-app-primary-500 text-white text-sm font-medium hover:opacity-90 disabled:opacity-50"
            >
              {saving ? <Loader2 className="w-4 h-4 animate-spin" /> : <Plus className="w-4 h-4" />}
              {t('english.vocab.save')}
            </button>
          </form>
        </CardContent>
      </Card>
    </div>
  )
}
